import { SimpleExpression } from './expression';

// ===== Enums =====

export enum FeeRuleType {
  Final = 'Final',
  IncludeFee = 'IncludeFee',
  Initial = 'Initial',
  RequestValue = 'RequestValue',
}

export enum FeeCostType {
  Fixed = 'Fixed',
  Percentage = 'Percentage',
}

export enum PaidTo {
  Affiliate = 'Affiliate',
  Broker = 'Broker',
  Investor = 'Investor',
  Lender = 'Lender',
  None = 'None',
  Other = 'Other',
  Seller = 'Seller',
}

export enum DiscountType {
  None = 'None',
  Reissue = 'Reissue',
  Refinance = 'Refinance',
  Both = 'Both',
}

export enum FeeToleranceLevel {
  None = 'None',
  Zero = 'Zero',
  TenPercent = 'TenPercent',
}

export enum FeesDocumentType {
  PDF = 'PDF',
}

export enum FeeUpdateType {
  AddValue = 'AddValue',
  RemoveValue = 'RemoveValue',
  SetValue = 'SetValue',
}

export enum IntegrationVendor {
  Argyle = 'Argyle',
  Empower = 'Empower',
  Encompass = 'Encompass',
  Equifax = 'Equifax',
  Ernst = 'Ernst',
  Experian = 'Experian',
  Factual = 'Factual',
  FannieMae = 'FannieMae',
  FreddieMac = 'FreddieMac',
  InformativeResearch = 'InformativeResearch',
  MeridianLink = 'MeridianLink',
  Mezzo = 'Mezzo',
  None = 'None',
  RatesBoard = 'RatesBoard',
  Resend = 'Resend',
  SmartFees = 'SmartFees',
  Snap = 'Snap',
  Truework = 'Truework',
  Truv = 'Truv',
  Vesta = 'Vesta',
  Xactus = 'Xactus',
}

// ===== View Models =====

export interface FeeConfigurationViewModel {
  feeConfigurationId: number;
  feeConfigurationGuid: string;
  feeRulesVersionId: number | null;
  /**
   * The HUD line this configuration produces a fee for. Null indicates a global configuration
   * (DEV-3720) whose rules apply request-wide and which produces no fee line of its own.
   */
  feeHudLineId: number | null;
  /**
   * The config group this configuration belongs to (DEV-3719). Null means the configuration is
   * ungrouped and always evaluated.
   */
  feeConfigGroupId: number | null;
  /** True when this configuration is a global configuration not tied to a HUD line (DEV-3720). */
  isGlobal: boolean;
  description: string | null;
  rules: FeeRuleViewModel[] | null;
}

/**
 * Read model for a fee configuration group (DEV-3719). Groups form a hierarchy (via
 * parentFeeConfigGroupId) whose optional qualifying expression determines whether the group and
 * its configurations apply to a request.
 */
export interface FeeConfigGroupViewModel {
  feeConfigGroupId: number;
  feeConfigGroupGuid: string;
  feeRulesVersionId: number | null;
  parentFeeConfigGroupId: number | null;
  name: string;
  /**
   * Optional expression determining whether this group applies to a request. Null or empty means
   * the group always applies to requests that reach it.
   */
  qualifyingExpression: string | null;
  sortOrder: number;
}

export interface FeeHudLineViewModel {
  feeHudLineId: number;
  feeRulesVersionId: number | null;
  losVendor: IntegrationVendor | null;
  hudLine: string | null;
  hudSection: string | null;
  description: string | null;
  loanEstimateSection: string | null;
  closingDisclosureSection: string | null;
  group: string | null;
  groupDetails: string | null;
  isAprAffiliate: boolean;
  isAprNonAffiliate: boolean;
  estimateIncludedInEscrow: boolean;
  borrowerCanShopFor: boolean;
  paidTo: PaidTo | null;
  restrictions: FeeRestrictionsViewModel | null;
  losMetadata: FeeLosMetadataConfigurationViewModel[] | null;
}

export interface FeeRuleViewModel {
  ruleId: string;
  description: string | null;
  expression: SimpleExpression | null;
  ruleType: FeeRuleType | null;
  settingData: SettingDataViewModel[] | null;
}

export interface DateValueViewModel {
  date: string | null;
  value: string | null;
}

export interface SettingDataViewModel {
  fieldId: string | null;
  value: string | null;
  updateType?: FeeUpdateType;
  isDateBased?: boolean;
  dateFieldPath?: string | null;
  dateValues?: DateValueViewModel[];
  setToEmpty?: boolean;
}

export interface FeeRulesVersionViewModel {
  feeRulesVersionId: number;
  versionNumber: number;
  description: string | null;
  publishDate: string | null;
  feeConfig: FeeConfigurationViewModel[] | null;
}

export interface FeeRestrictionsViewModel {
  toleranceLevel: FeeToleranceLevel;
  canEditBorrowerAmount: boolean;
  canEditSellerAmount: boolean;
  canEditBrokerAmount: boolean;
  canEditLenderAmount: boolean;
  canEditIncrementDollarAmount: boolean;
  canEditOtherAmount: boolean;
  canClientIgnore: boolean;
}

export interface FeeLosMetadataConfigurationViewModel {
  losFieldId: string | null;
  fieldName: string | null;
  value: string | null;
}

export interface FeeLosMetadataValueViewModel {
  losFieldId: string | null;
  value: string | null;
}

export interface FeeChargesViewModel {
  totalFeeAmount: number;
  totalFeeCostType: FeeCostType;
  totalFeePercentBasedOn: string | null;
  totalFeePercentage: number | null;
  minFeeAmount: number | null;
  maxFeeAmount: number | null;
  borrowerFeeCostType: FeeCostType;
  borrowerFeeAmount: number;
  borrowerFeePercentage: number | null;
  borrowerPaidOutsideClosing: number;
  sellerFeeCostType: FeeCostType;
  sellerFeeAmount: number;
  sellerFeePercentage: number | null;
  sellerPaidOutsideClosing: number;
  thirdPartyFeeCostType: FeeCostType;
  thirdPartyFeeAmount: number;
  thirdPartyFeePercentage: number | null;
  lenderFeeCostType: FeeCostType;
  lenderFeeAmount: number;
  lenderFeePercentage: number | null;
}

export interface FeeViewModel {
  feeConfigurationId: number | null;
  hudLine: string | null;
  hudSection: string | null;
  description: string | null;
  loanEstimateSection: string | null;
  closingDisclosureSection: string | null;
  group: string | null;
  groupDetails: string | null;
  isApr: boolean;
  estimateIncludedInEscrow: boolean;
  borrowerCanShopFor: boolean;
  paidTo: PaidTo;
  alwaysReturnFeeInResponse: boolean;
  isFeeValid: boolean;
  serviceProviderId: string | null;
  serviceType: string | null;
  charges: FeeChargesViewModel;
  restrictions: FeeRestrictionsViewModel;
  losMetadataValues: FeeLosMetadataValueViewModel[] | null;
}

export interface FeesDocumentViewModel {
  name: string | null;
  base64Data: string | null;
  documentType: FeesDocumentType;
}

export interface AppraisalProductViewModel {
  productId: string | null;
  productDescription: string | null;
}

export interface PropertyTaxBillViewModel {
  installments: TaxInstallmentsViewModel;
}

export interface TaxInstallmentsViewModel {
  numberOfInstallments: number | null;
  firstInstallmentAmount: number | null;
  secondInstallmentAmount: number | null;
  thirdInstallmentAmount: number | null;
  fourthInstallmentAmount: number | null;
}

export interface TaxPayeeViewModel {
  payeeId: string | null;
  name: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

export interface TaxEscrowReservesViewModel {
  months: number | null;
  amount: number | null;
}

export interface PropertyTaxLineViewModel {
  taxAmount: number | null;
  lineType: string | null;
  taxYear: number | null;
  priorYearDue: number | null;
  projectedTax: number | null;
  supplementalTax: number | null;
  receivedDate: string | null;
  dueDate: string | null;
  delinquent: boolean;
  propertyExemption: string | null;
  paymentStatus: string | null;
  taxPayee: TaxPayeeViewModel | null;
  taxBill: PropertyTaxBillViewModel[] | null;
  escrowReserves: TaxEscrowReservesViewModel | null;
}

export interface PropertyTaxesViewModel {
  taxLines: PropertyTaxLineViewModel[] | null;
}

export interface EndorsementFeeViewModel {
  amount: number;
  name: string | null;
}

export interface ServiceProviderOptionViewModel {
  providerId: string | null;
  serviceCategory: string | null;
  name: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
}

export interface SettlementServiceProviderViewModel {
  providerId: string | null;
  serviceCategory: string | null;
  name: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  appliedDiscountType: DiscountType;
  isAffiliate: boolean;
  endorsementFees: EndorsementFeeViewModel[] | null;
  serviceProviderOptions: ServiceProviderOptionViewModel[] | null;
  losMetadataValues: FeeLosMetadataValueViewModel[] | null;
}

export interface ProviderResponseViewModel {
  providerName: string | null;
  responseData: string | null;
}

export interface LoanFeesSupplementalDataViewModel {
  providerTransactionId: string | null;
  appraisalProducts: AppraisalProductViewModel[] | null;
  propertyTaxes: PropertyTaxesViewModel | null;
  serviceProviders: SettlementServiceProviderViewModel[] | null;
  providerResponses: ProviderResponseViewModel[] | null;
  documents: FeesDocumentViewModel[] | null;
}

export interface FeesResponseViewModel {
  transactionId: string | null;
  fees: FeeViewModel[] | null;
  supplementalData: LoanFeesSupplementalDataViewModel | null;
}

export interface FeeResponseViewModel {
  transactionId: string | null;
  productName: string | null;
  fees: FeeViewModel[] | null;
  supplementalData: LoanFeesSupplementalDataViewModel | null;
  errors: string[] | null;
}

// ===== Question Response Defaults =====

export interface QuestionResponseDefaultViewModel {
  questionResponseDefaultId: number;
  questionId: number;
  questionKey: string | null;
  vendor: IntegrationVendor;
  defaultAnswer: boolean;
  defaultValue: string | null;
  createdDate: string;
  createdBy: string;
  updatedDate: string | null;
  updatedBy: string | null;
}

export interface CreateOrUpdateQuestionResponseDefaultViewModel {
  questionResponseDefaultId?: number | null;
  questionId: number;
  defaultAnswer: boolean;
  defaultValue: string | null;
}

// ===== Request Models =====

/**
 * Internal-only settings DTO for the Fees Emulator. Mirrors
 * `Core.Api.Models.Fees.Request.EmulatorFeesRequestSettingsDto` from the Mezzo Service spec.
 * `feeRulesVersionId` is required by the server; supplying it pins fee resolution to that
 * exact publication regardless of `asOfDate`.
 */
export interface EmulatorFeesRequestSettingsDto {
  feeRulesVersionId: number;
  asOfDate?: string | null;
  useUnpublishedConfiguration?: boolean;
  includeRawThirdPartyResponses?: boolean;
  selectedServiceProviders?: unknown[] | null;
  serviceProviderFeeUpdates?: unknown[] | null;
}

/**
 * Internal-only request DTO for `POST /fees/emulator/single`. Payload shape mirrors the public
 * fee request; nested DTOs are passthrough (`unknown`) because centro-app does not currently
 * model the public `FeesRequestSettingsDto`/`FeesRequestDto` family — the emulator UI edits the
 * payload as JSON via Monaco.
 */
export interface EmulatorFeesRequestDto {
  loanInformation?: unknown;
  subjectProperty?: unknown;
  borrowerInformation?: unknown;
  productInformation?: unknown;
  settings: EmulatorFeesRequestSettingsDto;
  vendorOptions?: unknown;
}


export interface CreateOrUpdateFeeConfigRequestViewModel {
  /**
   * The HUD line this configuration produces a fee for. Null creates a global configuration
   * (DEV-3720) whose rules apply request-wide and which produces no fee line of its own.
   */
  feeHudLineId: number | null;
  /**
   * The config group this configuration belongs to (DEV-3719). Null leaves the configuration
   * ungrouped (always evaluated).
   */
  feeConfigGroupId: number | null;
  description: string | null;
  rules: FeeRuleViewModel[] | null;
}

/**
 * Request model for creating or updating a fee configuration group (DEV-3719).
 * feeRulesVersionId is intentionally excluded as it can only be set by the publication service.
 */
export interface CreateOrUpdateFeeConfigGroupViewModel {
  parentFeeConfigGroupId: number | null;
  name: string;
  qualifyingExpression: string | null;
  sortOrder: number;
}

export interface CreateOrUpdateFeeHudLineViewModel {
  losVendor: IntegrationVendor;
  hudLine: string;
  hudSection: string;
  description: string;
  loanEstimateSection: string;
  closingDisclosureSection: string;
  group: string | null;
  groupDetails: string | null;
  isAprAffiliate: boolean;
  isAprNonAffiliate: boolean;
  estimateIncludedInEscrow: boolean;
  borrowerCanShopFor: boolean;
  paidTo: PaidTo;
  restrictions: FeeRestrictionsViewModel;
  losMetadata: FeeLosMetadataConfigurationViewModel[];
}

export interface PublishFeeRulesVersionRequestViewModel {
  description: string;
  publishDate?: string | null;
}

// ===== Helper Types =====

export function isDraftConfiguration(config: FeeConfigurationViewModel): boolean {
  return config.feeRulesVersionId === null;
}

export function isDraftHudLine(hudLine: FeeHudLineViewModel): boolean {
  return hudLine.feeRulesVersionId === null;
}

export function isPublishedConfiguration(config: FeeConfigurationViewModel): boolean {
  return config.feeRulesVersionId !== null;
}

export function isPublishedHudLine(hudLine: FeeHudLineViewModel): boolean {
  return hudLine.feeRulesVersionId !== null;
}

// ===== Import Models =====

export interface ExcelImportRowError {
  rowNumber: number;
  errorMessage: string | null;
  rowIdentifier: string | null;
}

export interface ExcelImportResult {
  success: boolean;
  totalRows: number;
  successfulRows: number;
  failedRows: number;
  errors: ExcelImportRowError[] | null;
  warnings: ExcelImportRowError[] | null;
}

// ===== Migration Models =====

export interface FeeHudLineExport {
  hudLine: string;
  hudSection: string;
  description: string;
  loanEstimateSection: string;
  closingDisclosureSection: string;
  losVendor: IntegrationVendor;
  group: string | null;
  groupDetails: string | null;
  isAprAffiliate: boolean;
  isAprNonAffiliate: boolean;
  estimateIncludedInEscrow: boolean;
  borrowerCanShopFor: boolean;
  paidTo: PaidTo | null;
  restrictions: FeeRestrictionsViewModel;
  losMetadata: FeeLosMetadataConfigurationViewModel[];
}

export interface FeeConfigurationExport {
  feeConfigurationGuid: string;
  hudLine: string;
  feeConfigGroupGuid?: string | null;
  description: string | null;
  rules: FeeRuleExport[];
}

export interface FeeConfigGroupExport {
  feeConfigGroupGuid: string;
  parentFeeConfigGroupGuid: string | null;
  name: string;
  qualifyingExpression: string | null;
  sortOrder: number;
}

export interface FeeRuleExport {
  ruleId: string;
  description: string;
  expression: string | null;
  ruleType: FeeRuleType;
  settingData: SettingDataExport[];
}

export interface SettingDataExport {
  fieldId: string;
  value: string | null;
  updateType: FeeUpdateType;
  isDateBased?: boolean;
  dateFieldPath?: string | null;
  dateValues?: DateValueViewModel[];
}

export interface FeeMigrationExport {
  hudLines: FeeHudLineExport[];
  configurations: FeeConfigurationExport[];
  configGroups: FeeConfigGroupExport[];
}

export enum DiffAction {
  Insert = 'Insert',
  Update = 'Update',
  Delete = 'Delete',
  Unchanged = 'Unchanged',
}

export interface FeeMigrationHudLineDiff {
  action: DiffAction;
  hudLine: string;
  imported: FeeHudLineExport | null;
  current: FeeHudLineExport | null;
  changedFields: string[];
}

export interface FeeMigrationRuleDiff {
  action: DiffAction;
  ruleId: string;
  description: string;
  imported: FeeRuleExport | null;
  current: FeeRuleExport | null;
  changedFields: string[];
}

export interface FeeMigrationConfigDiff {
  action: DiffAction;
  feeConfigurationGuid: string;
  hudLine: string;
  imported: FeeConfigurationExport | null;
  current: FeeConfigurationExport | null;
  changedFields: string[];
  ruleDiffs: FeeMigrationRuleDiff[];
}

export interface FeeMigrationConfigGroupDiff {
  action: DiffAction;
  feeConfigGroupGuid: string;
  name: string;
  imported: FeeConfigGroupExport | null;
  current: FeeConfigGroupExport | null;
  changedFields: string[];
}

export interface FeeMigrationDiff {
  hudLineDiffs: FeeMigrationHudLineDiff[];
  configurationDiffs: FeeMigrationConfigDiff[];
  configGroupDiffs: FeeMigrationConfigGroupDiff[];
}

export interface FeeMigrationImportRequest {
  hudLineDiffs: FeeMigrationHudLineDiff[];
  configurationDiffs: FeeMigrationConfigDiff[];
  configGroupDiffs: FeeMigrationConfigGroupDiff[];
}

export interface FeeMigrationImportResult {
  success: boolean;
  hudLinesInserted: number;
  hudLinesUpdated: number;
  hudLinesDeleted: number;
  configurationsInserted: number;
  configurationsUpdated: number;
  configurationsDeleted: number;
  configGroupsInserted: number;
  configGroupsUpdated: number;
  configGroupsDeleted: number;
  errors: string[];
}
